<template>
  <v-main>
    <v-container fluid>
      <!-- Page Header Card -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="6">
                  <h3 class="text-h5 font-weight-bold primary--text mb-2">
                    <v-icon left color="primary">
                      {{ editMode ? 'mdi-pencil' : 'mdi-eye' }}
                    </v-icon>
                    {{ editMode ? 'Edit Language' : 'View Language' }}
                  </h3>
                  <p class="text-subtitle-1 text--secondary mb-0">
                    {{ editMode ? 'Modify language information' : 'Language details and information' }}
                  </p>
                </v-col>
                <v-col cols="12" md="6" class="text-right">
                  <v-btn
                    text
                    :to="{ name: 'languages' }"
                    class="mr-2"
                  >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back to List
                  </v-btn>
                  <v-btn
                    v-if="!editMode"
                    color="primary"
                    @click="toggleEditMode"
                    class="mr-2"
                  >
                    <v-icon left>mdi-pencil</v-icon>
                    Edit
                  </v-btn>
                  <v-btn
                    v-if="editMode"
                    text
                    @click="cancelEdit"
                    class="mr-2"
                  >
                    <v-icon left>mdi-close</v-icon>
                    Cancel
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="loading">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text class="text-center py-12">
              <health-loader message="Loading language details..." />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Content -->
      <v-row v-else-if="language">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-card-text class="pa-6">
                <!-- Language Status Banner -->
                <v-alert
                  v-if="language.is_deleted"
                  type="warning"
                  prominent
                  border="left"
                  class="mb-6"
                >
                  <v-row align="center">
                    <v-col>
                      <div class="text-h6">Language Deleted</div>
                      <div>This language has been soft deleted and is not available for use.</div>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="success"
                        @click="restoreLanguage"
                        :loading="restoring"
                      >
                        <v-icon left>mdi-restore</v-icon>
                        Restore
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-alert>

                <!-- Basic Information Section -->
                <div class="mb-6">
                  <h4 class="text-h6 font-weight-bold primary--text mb-4">
                    <v-icon left color="primary">mdi-information</v-icon>
                    Basic Information
                  </h4>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.code"
                        label="Language Code *"
                        prepend-inner-icon="mdi-code-tags"
                        solo
                        rounded
                        :readonly="!editMode"
                        :rules="editMode ? codeRules : []"
                        :error-messages="errors.code"
                        hint="ISO language code (e.g., en, hi, ta)"
                        persistent-hint
                        counter="10"
                        @input="clearFieldError('code')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.name"
                        label="Language Name *"
                        prepend-inner-icon="mdi-translate"
                        solo
                        rounded
                        :readonly="!editMode"
                        :rules="editMode ? nameRules : []"
                        :error-messages="errors.name"
                        hint="English name of the language"
                        persistent-hint
                        counter="100"
                        @input="clearFieldError('name')"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.native_name"
                        label="Native Name"
                        prepend-inner-icon="mdi-alphabet-latin"
                        solo
                        rounded
                        :readonly="!editMode"
                        :error-messages="errors.native_name"
                        hint="Language name in its own script (optional)"
                        persistent-hint
                        counter="100"
                        @input="clearFieldError('native_name')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        color="success"
                        inset
                        :readonly="!editMode"
                        hide-details
                      >
                        <template v-slot:prepend>
                          <v-icon color="success" class="mr-2">
                            {{ form.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                          </v-icon>
                        </template>
                      </v-switch>
                      <div class="text-caption text--secondary mt-1">
                        {{ form.is_active ? 'Language is active and available for use' : 'Language is inactive and not available' }}
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Metadata Section -->
                <div class="mb-6">
                  <h4 class="text-h6 font-weight-bold primary--text mb-4">
                    <v-icon left color="primary">mdi-database</v-icon>
                    Metadata
                  </h4>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="language.id"
                        label="Language ID"
                        prepend-inner-icon="mdi-identifier"
                        solo
                        rounded
                        readonly
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="language.created_by?.user_name || 'N/A'"
                        label="Created By"
                        prepend-inner-icon="mdi-account"
                        solo
                        rounded
                        readonly
                        hide-details
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row class="mt-3">
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="formatDate(language.created_at)"
                        label="Created At"
                        prepend-inner-icon="mdi-calendar-plus"
                        solo
                        rounded
                        readonly
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="formatDate(language.updated_at)"
                        label="Last Updated"
                        prepend-inner-icon="mdi-calendar-edit"
                        solo
                        rounded
                        readonly
                        hide-details
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Language Preview Section (if not editing) -->
                <div v-if="!editMode" class="mb-6">
                  <h4 class="text-h6 font-weight-bold primary--text mb-4">
                    <v-icon left color="primary">mdi-eye</v-icon>
                    Language Preview
                  </h4>
                  
                  <v-card outlined class="pa-4">
                    <div class="text-center">
                      <v-chip
                        :color="form.is_active ? 'primary' : 'grey'"
                        text-color="white"
                        large
                        class="mb-3"
                      >
                        <strong>{{ form.code }}</strong>
                      </v-chip>
                      <div class="text-h5 font-weight-bold mb-2">{{ form.name }}</div>
                      <div v-if="form.native_name" class="text-h6 text--secondary mb-2">
                        {{ form.native_name }}
                      </div>
                      <v-chip
                        :color="form.is_active ? 'success' : 'error'"
                        text-color="white"
                        small
                      >
                        {{ form.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </div>
                  </v-card>
                </div>
              </v-card-text>

              <!-- Form Actions (only in edit mode) -->
              <v-card-actions v-if="editMode" class="pa-6 pt-0">
                <v-spacer></v-spacer>
                <v-btn
                  large
                  text
                  @click="cancelEdit"
                  class="mr-2"
                >
                  Cancel
                </v-btn>
                <v-btn
                  large
                  color="primary"
                  @click="saveLanguage"
                  :loading="saving"
                  :disabled="!valid"
                  elevation="2"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save Changes
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error State -->
      <v-row v-else>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text class="text-center py-12">
              <v-icon size="64" color="error">mdi-alert</v-icon>
              <div class="text-h6 mt-4">Language not found</div>
              <div class="text--secondary">The requested language could not be found.</div>
              <v-btn
                color="primary"
                :to="{ name: 'languages' }"
                class="mt-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back to Languages
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LanguageEdit',
  data() {
    return {
      loading: false,
      saving: false,
      restoring: false,
      valid: false,
      editMode: false,
      language: null,
      form: {
        code: '',
        name: '',
        native_name: '',
        is_active: true
      },
      originalForm: {},
      errors: {},
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      codeRules: [
        v => !!v || 'Language code is required',
        v => (v && v.length >= 2) || 'Language code must be at least 2 characters',
        v => (v && v.length <= 10) || 'Language code must be less than 10 characters',
        v => /^[a-z]{2,3}(-[A-Z]{2})?$/.test(v) || 'Invalid language code format (e.g., en, hi, en-US)'
      ],
      nameRules: [
        v => !!v || 'Language name is required',
        v => (v && v.length >= 2) || 'Language name must be at least 2 characters',
        v => (v && v.length <= 100) || 'Language name must be less than 100 characters'
      ]
    }
  },
  mounted() {
    this.fetchLanguage()
  },
  methods: {
    async fetchLanguage() {
      this.loading = true
      try {
        const response = await axios.get(`/api/languages/${this.$route.params.id}`)
        
        if (response.data.success) {
          this.language = response.data.data
          this.fillForm()
        } else {
          this.showSnackbar('Failed to fetch language details', 'error')
        }
      } catch (error) {
        console.error('Error fetching language:', error)
        this.showSnackbar('Error fetching language details', 'error')
      } finally {
        this.loading = false
      }
    },
    fillForm() {
      this.form = {
        code: this.language.code || '',
        name: this.language.name || '',
        native_name: this.language.native_name || '',
        is_active: this.language.is_active || false
      }
      this.saveOriginalForm()
    },
    saveOriginalForm() {
      this.originalForm = { ...this.form }
    },
    toggleEditMode() {
      this.editMode = true
      this.saveOriginalForm()
    },
    cancelEdit() {
      this.editMode = false
      this.form = { ...this.originalForm }
      this.errors = {}
    },
    clearFieldError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field)
      }
    },
    async saveLanguage() {
      if (!this.$refs.form.validate()) {
        this.showSnackbar('Please correct the errors in the form', 'error')
        return
      }

      this.saving = true
      this.errors = {}

      try {
        const response = await axios.put(`/api/languages/${this.language.id}`, this.form)
        
        if (response.data.success) {
          this.language = response.data.data
          this.fillForm()
          this.editMode = false
          this.showSnackbar('Language updated successfully', 'success')
        } else {
          this.showSnackbar(response.data.message || 'Failed to update language', 'error')
        }
      } catch (error) {
        console.error('Error updating language:', error)
        
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors
          this.showSnackbar('Please correct the validation errors', 'error')
        } else {
          this.showSnackbar('Error updating language', 'error')
        }
      } finally {
        this.saving = false
      }
    },
    async restoreLanguage() {
      this.restoring = true
      try {
        const response = await axios.post(`/api/languages/restore/${this.language.id}`)
        
        if (response.data.success) {
          this.language.is_deleted = false
          this.showSnackbar('Language restored successfully', 'success')
        } else {
          this.showSnackbar('Failed to restore language', 'error')
        }
      } catch (error) {
        console.error('Error restoring language:', error)
        this.showSnackbar('Error restoring language', 'error')
      } finally {
        this.restoring = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString()
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s ease;
}

.text-h6 {
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.v-text-field >>> .v-input__control .v-input__slot {
  min-height: 48px;
}

.v-text-field--readonly >>> .v-input__control .v-input__slot {
  background-color: #f5f5f5 !important;
}

.v-switch >>> .v-input__control {
  width: auto;
}

.v-switch--readonly {
  pointer-events: none;
}

.language-preview {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
}
</style>